import { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
  Fab,
} from '@mui/material'
import axios from 'axios'
import { useSnackbar } from 'notistack'
import { Save } from '@mui/icons-material'
import { User } from '../../types'
import {
  TeamPermissionsComponent,
  UserPermissions,
  UserOverrideState,
} from '../components/abilities'
import {
  TeamPermissions as TeamPermissionsType,
  DEFAULT_PERMISSIONS,
} from '../components/abilities/PermissionCheckboxList'
import { CONTAINER_MAX_WIDTH } from '../constants'

interface UserOverride {
  user: string | { id: string; name?: string; email?: string }
  permissions: Array<TeamPermissionsType & { blockType: 'permissions' }>
}

interface TeamPermissionsDoc {
  id: string
  team: string
  permissions: Array<TeamPermissionsType & { blockType: 'permissions' }>
  userOverrides?: UserOverride[]
}

export function TeamSettings() {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const [loading, setLoading] = useState(false)
  const [members, setMembers] = useState<User[]>([])
  const [permissionsDocId, setPermissionsDocId] = useState<string | null>(null)
  const [teamPermissions, setTeamPermissions] =
    useState<TeamPermissionsType>(DEFAULT_PERMISSIONS)
  const [userOverrides, setUserOverrides] = useState<{
    [userId: string]: UserOverrideState
  }>({})
  const { enqueueSnackbar } = useSnackbar()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [settingsRes, membersRes] = await Promise.all([
        axios.get('/owned-team-settings'),
        axios.get('/api-active-team'),
      ])

      if (settingsRes.data.success && settingsRes.data.permissions) {
        const doc = settingsRes.data.permissions as TeamPermissionsDoc
        setPermissionsDocId(doc.id)

        // Takım izinlerini parse et
        if (doc.permissions?.[0]) {
          const { blockType, ...perms } = doc.permissions[0]
          setTeamPermissions(perms as TeamPermissionsType)
        }

        // Kullanıcı override'larını parse et - API'den gelen user bilgisini kullan
        if (doc.userOverrides?.length) {
          const overrides: { [userId: string]: UserOverrideState } = {}
          doc.userOverrides.forEach((override) => {
            if (override.permissions?.[0]) {
              const { blockType, ...perms } = override.permissions[0]
              // User string veya object olabilir (populate edilmiş/edilmemiş)
              const userId = typeof override.user === 'string' ? override.user : override.user.id
              const userObj = typeof override.user === 'object' ? override.user : { id: override.user }
              overrides[userId] = {
                user: userObj,
                permissions: perms as TeamPermissionsType,
              }
            }
          })
          setUserOverrides(overrides)
        }
      }
      setMembers(membersRes.data.data.members || [])
    } catch (error: any) {
      enqueueSnackbar('Veriler yüklenemedi', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setLoading(true)

      // Kullanıcı override'larını API formatına dönüştür (sadece user id ve permissions gönder)
      const userOverridesArray = Object.entries(userOverrides).map(
        ([userId, overrideState]) => ({
          user: userId,
          permissions: [
            {
              blockType: 'permissions' as const,
              ...overrideState.permissions,
            },
          ],
        }),
      )

      await axios.post('/owned-team-settings', {
        permissions: [
          {
            blockType: 'permissions',
            ...teamPermissions,
          },
        ],
        userOverrides: userOverridesArray,
      })

      // Başarılı kayıt sonrası güncel veriyi çek
      await loadData()

      enqueueSnackbar('Ayarlar kaydedildi', { variant: 'success' })
    } catch (error: any) {
      enqueueSnackbar('Kaydetme hatası', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleTeamPermissionToggle = (
    key: keyof TeamPermissionsType,
    value: boolean,
  ) => {
    setTeamPermissions((prev) => ({
      ...prev,
      [key]: value,
    }))
  }

  const handleUserPermissionToggle = (
    userId: string,
    key: keyof TeamPermissionsType,
    value: boolean,
  ) => {
    setUserOverrides((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        permissions: {
          ...prev[userId].permissions,
          [key]: value,
        },
      },
    }))
  }

  const handleAddUserOverride = (userId: string) => {
    const member = members.find((m) => m.id === userId)
    setUserOverrides((prev) => ({
      ...prev,
      [userId]: {
        user: { id: userId, name: member?.name, email: member?.email },
        permissions: { ...DEFAULT_PERMISSIONS },
      },
    }))
    enqueueSnackbar(
      `${member?.name || member?.email || 'Kullanıcı'} için özel izinler eklendi`,
      { variant: 'success' },
    )
  }

  const handleRemoveUserOverride = (userId: string) => {
    const overrideState = userOverrides[userId]
    setUserOverrides((prev) => {
      const { [userId]: _, ...rest } = prev
      return rest
    })
    enqueueSnackbar(
      `${overrideState?.user?.name || overrideState?.user?.email || 'Kullanıcı'} için özel izinler kaldırıldı`,
      { variant: 'success' },
    )
  }

  const handleClearAllUserOverrides = () => {
    setUserOverrides({})
    enqueueSnackbar('Tüm kullanıcı özel izinleri kaldırıldı', { variant: 'success' })
  }

  if (loading && !permissionsDocId) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <Typography>Yükleniyor...</Typography>
      </Container>
    )
  }

  return (
    <Box
      sx={{
        height: isMobile
          ? 'auto'
          : `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
        display: 'flex',
        flexDirection: 'column',
        overflow: isMobile ? 'visible' : 'hidden',
        bgcolor: 'background.default',
        minHeight: isMobile ? '100vh' : 'auto',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: isMobile ? 'visible' : 'hidden',
          px: 2,
          py: 2,
        }}
      >
        <Container
          maxWidth={CONTAINER_MAX_WIDTH}
          sx={{
            height: isMobile ? 'auto' : '100%',
            display: 'flex',
            flexDirection: 'column',
            p: 0,
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              flex: isMobile ? 'none' : 1,
              height: isMobile ? 'auto' : '100%',
              overflow: isMobile ? 'visible' : 'hidden',
              margin: 0,
              width: '100%',
              '& .MuiGrid-item': {
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                height: isMobile ? 'auto' : '100%',
              },
              '& .MuiGrid-container': {
                height: isMobile ? 'auto' : '100%',
              },
            }}
          >
            {/* Takım İzinleri - Sol */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{ minHeight: 0, height: isMobile ? 'auto' : '100%' }}
            >
              <Box
                sx={{
                  height: isMobile ? 'auto' : '100%',
                  mb: isMobile ? 2 : 0,
                }}
              >
                <TeamPermissionsComponent
                  permissions={teamPermissions}
                  onToggle={handleTeamPermissionToggle}
                  disabled={loading}
                  isMobile={isMobile}
                />
              </Box>
            </Grid>

            {/* Kullanıcı İzinleri - Sağ */}
            <Grid
              item
              xs={12}
              lg={6}
              sx={{ minHeight: 0, height: isMobile ? 'auto' : '100%' }}
            >
              <Box
                sx={{
                  height: isMobile ? 'auto' : '100%',
                  mb: isMobile ? 2 : 0,
                }}
              >
                <UserPermissions
                  userOverrides={userOverrides}
                  members={members}
                  onToggleUserPermission={handleUserPermissionToggle}
                  onAddUserOverride={handleAddUserOverride}
                  onRemoveUserOverride={handleRemoveUserOverride}
                  onClearAll={handleClearAllUserOverrides}
                  isMobile={isMobile}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Floating Action Button - Kaydet */}
      <Fab
        color="primary"
        aria-label="kaydet"
        onClick={saveSettings}
        disabled={loading}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Save />
      </Fab>
    </Box>
  )
}
